import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HumanCheck from '../components/HumanCheck';
import { createApiUrl } from '../utils/apiConfig';
import styles from './Register.module.css';

const Register = () => {
  const navigate = useNavigate();
  const initialFormData = {
    contactName: '',
    email: '',
    country: 'United Kingdom',
    postZipCode: '',
    schoolName: '',
    address: '',
    orgCode: '',
    password: '',
    passwordConfirmation: '',
  };
  const [formData, setFormData] = useState(initialFormData);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [schools, setSchools] = useState([]);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [humanCheckValid, setHumanCheckValid] = useState(false);

  useEffect(() => {
    setFormData(initialFormData);
    // Explicitly clear input values in the DOM to defeat browser autofill
    const ids = ['email', 'password', 'passwordConfirmation'];
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.passwordConfirmation) {
      setError('Passwords do not match. Please try again.');
      // Clear password fields
      setFormData({ ...formData, password: '', passwordConfirmation: '' });
      document.getElementById('password').value = '';
      document.getElementById('passwordConfirmation').value = '';
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long. Please try again.');
      setFormData({ ...formData, password: '', passwordConfirmation: '' });
      document.getElementById('password').value = '';
      document.getElementById('passwordConfirmation').value = '';
      return;
    }

    if (!humanCheckValid) {
      setError('Please answer the human verification question correctly.');
      return;
    }

    try {
      // Prepare data for backend (backend expects 'username' instead of 'email')
      const registrationData = {
        ...formData,
        username: formData.email, // Backend expects 'username' field
        schoolEmail: formData.email // Also send as schoolEmail
      };
      
      const response = await axios.post(createApiUrl('/api/register/school'), registrationData);
      if (response.data.success) {
        setError(''); // Clear any previous errors
        // Redirect to login page
        navigate('/login');
      }
      // Clear sensitive fields after submit
      setFormData(initialFormData);
      document.getElementById('email').value = '';
      document.getElementById('password').value = '';
      document.getElementById('passwordConfirmation').value = '';
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      setFormData({ ...formData, password: '', passwordConfirmation: '' });
      document.getElementById('password').value = '';
      document.getElementById('passwordConfirmation').value = '';
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCountryChange = (e) => {
    setFormData({
      ...formData,
      country: e.target.value,
      postZipCode: '',
      schoolName: '',
      address: ''
    });
    setSchools([]);
    setShowSchoolDropdown(false);
  };

  const handlePostcodeChange = async (e) => {
    const postcode = e.target.value;
    setFormData({
      ...formData,
      postZipCode: postcode
    });

    if (postcode.length > 0 && formData.country === 'United Kingdom') {
      setLoadingSchools(true);
      try {
        const response = await axios.get(createApiUrl(`/api/schools?search=${postcode}&country=United Kingdom`));
        setSchools(response.data);
        setShowSchoolDropdown(response.data.length > 0);
      } catch (error) {
        console.error('Error fetching schools:', error);
        setSchools([]);
        setShowSchoolDropdown(false);
      } finally {
        setLoadingSchools(false);
      }
    } else {
      setSchools([]);
      setShowSchoolDropdown(false);
    }
  };

  const handleSchoolSelect = (school) => {
    setFormData({
      ...formData,
      schoolName: school.name,
      address: school.address,
      postZipCode: school.postZipCode
    });
    setShowSchoolDropdown(false);
  };

  const handleEmailBlur = async () => {
    if (formData.email) {
      try {
        const response = await axios.post(createApiUrl('/api/check-email'), { email: formData.email });
        if (response.data.exists) {
          setEmailError('Email already exists. Please use a different email.');
        } else {
          setEmailError('');
        }
      } catch (error) {
        console.error('Error checking email:', error);
      }
    }
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const togglePasswordConfirmation = () => {
    setShowPasswordConfirmation(!showPasswordConfirmation);
  };

  return (
    <div className={styles.registerSchoolContainer}>
      <h1 className={styles.registerSchoolHeading}>Register<br />School</h1>
      <div className={styles.registerSchoolCard}>
        {error && <div className="alert alert-danger">{error}</div>}
        <form id="registerForm" onSubmit={handleSubmit} className={styles.registerForm}>
          <div className={styles.formField}>
            <input
              type="text"
              id="contactName"
              name="contactName"
              className={styles.formInput}
              required
              placeholder="Contact name"
              value={formData.contactName}
              onChange={handleChange}
            />
            <label htmlFor="contactName" className={styles.formLabel}>Contact Name:</label>
          </div>
          <div className={styles.formField}>
            <input
              type="email"
              id="email"
              name="email"
              className={styles.formInput}
              required
              placeholder="School Email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              autoComplete="off"
            />
            <label htmlFor="email" className={styles.formLabel}>School Email:</label>
            {emailError && <div className={styles.errorText}>{emailError}</div>}
          </div>
          <div className={styles.formField}>
            <select
              id="country"
              name="country"
              className={styles.formSelect}
              required
              value={formData.country}
              onChange={handleCountryChange}
            >
              <option value="United Kingdom">United Kingdom</option>
              <option value="Africa">Africa</option>
              <option value="Canada">Canada</option>
              <option value="Caribbean">Caribbean</option>
              <option value="Europe">Europe</option>
              <option value="United States">United States</option>
            </select>
            <label htmlFor="country" className={styles.formLabel}>Country:</label>
          </div>
          <div className={`${styles.formField} ${styles.positionRelative}`}>
            <input
              type="text"
              id="postZipCode"
              name="postZipCode"
              className={styles.formInput}
              required
              placeholder="Postcode/Zip Code"
              value={formData.postZipCode}
              onChange={handlePostcodeChange}
            />
            <label htmlFor="postZipCode" className={styles.formLabel}>Postcode/Zip Code:</label>
            {showSchoolDropdown && (
              <div className={styles.schoolDropdown}>
                <div className={styles.dropdownHeader}>Select your school</div>
                {loadingSchools ? (
                  <div className={styles.schoolOption}><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Loading...</div>
                ) : (
                  schools.map((school, idx) => (
                    <div key={school._id || idx} className={styles.schoolOption} onClick={() => handleSchoolSelect(school)}>
                      <div className={styles.schoolName}>{school.name}</div>
                      <div className={styles.schoolAddress}>{school.address}</div>
                      <div className={styles.schoolPostcode}>{school.postZipCode}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div className={styles.formField}>
            <input
              type="text"
              id="schoolName"
              name="schoolName"
              className={styles.formInput}
              required
              placeholder="School Name"
              value={formData.schoolName}
              onChange={handleChange}
            />
            <label htmlFor="schoolName" className={styles.formLabel}>School Name:</label>
          </div>
          <div className={styles.formField}>
            <input
              type="text"
              id="address"
              name="address"
              className={styles.formInput}
              required
              placeholder="School Address"
              value={formData.address}
              onChange={handleChange}
            />
            <label htmlFor="address" className={styles.formLabel}>School Address:</label>
          </div>
          <div className={styles.formField}>
            <input
              type="text"
              id="orgCode"
              name="orgCode"
              className={styles.formInput}
              placeholder="Organization Code (optional)"
              value={formData.orgCode}
              onChange={handleChange}
            />
            <label htmlFor="orgCode" className={styles.formLabel}>Organization Code (optional):</label>
          </div>
          <div className={styles.formField}>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              className={styles.formInput}
              required
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="off"
            />
            <label htmlFor="password" className={styles.formLabel}>Password:</label>
            <button type="button" className={styles.togglePassword} onClick={togglePassword} tabIndex={-1}>
              <i className={`fas fa-${showPassword ? 'eye' : 'eye-slash'}`}></i>
            </button>
          </div>
          <div className={styles.formField}>
            <input
              type={showPasswordConfirmation ? "text" : "password"}
              id="passwordConfirmation"
              name="passwordConfirmation"
              className={styles.formInput}
              required
              placeholder="Confirm Password"
              value={formData.passwordConfirmation}
              onChange={handleChange}
              autoComplete="off"
            />
            <label htmlFor="passwordConfirmation" className={styles.formLabel}>Confirm Password:</label>
            <button type="button" className={styles.togglePassword} onClick={togglePasswordConfirmation} tabIndex={-1}>
              <i className={`fas fa-${showPasswordConfirmation ? 'eye' : 'eye-slash'}`}></i>
            </button>
          </div>

          {/* Human Check */}
          <div className={styles.formField}>
            <HumanCheck 
              onValidationChange={setHumanCheckValid}
              error={error && !humanCheckValid ? 'Please answer the question correctly to proceed.' : ''}
              formType="register"
            />
          </div>

          <button type="submit" className={styles.submitButton}>Register School</button>
        </form>
        <div className={styles.registerFooter}>
          <span>Already have an account? <Link to="/login">Login</Link></span>
          <br />
          <span>Do you want to register as an organization? <Link to="/register-org">Register Organization</Link></span>
        </div>
      </div>
    </div>
  );
};

export default Register; 